import { PropsWithChildren, useState, useRef } from "react";

import {
  createBrowserRouter,
  RouterProvider,
} from "react-router-dom";

import {
  QueryCache,
  QueryClient, QueryClientProvider
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { Box, CssBaseline, GlobalStyles } from "@mui/material";
import * as Sentry from "@sentry/react";

import {
  AuthLibMSALTokenAcquisitionError, MsalAuthenticationTemplate,
  MsalAuthErrorFallbackBoundary, MSALInteractionType, MsalLoader,
  MSALTenantedApp, useMSALPayload
} from '@initia-digital/auth-client-utils';
import { FilledButton, SnackbarProvider, ThemeProvider, Typography, lightProdTheme, useSnackbar } from '@initia-digital/component-library';

import { BasicLogger } from "@initia-digital/ts-utils";

import ErrorPage from './pages/errorPage';
import Index from './pages';
import JobDetail from "./pages/job/[jobId]";
import CreatePlanDetail from "./pages/job/[jobId]/plan";
import EditPlanDetail from "./pages/job/[jobId]/edit-plan/[planId]";
import ViewPlanDetail from "./pages/job/[jobId]/view-plan/[planId]";
import CreatePrestartDetail from "./pages/job/[jobId]/plan/[planId]/prestart";
import EditPrestartDetail from "./pages/job/[jobId]/plan/[planId]/edit-prestart/[prestartId]";

import { handleRestartApp } from "./utils/restartApp";
import { UpdateServiceWorkerProvider } from "./components/serviceWorkerUpdates";
import ViewPrestartDetail from "./pages/job/[jobId]/plan/[planId]/view-prestart/[prestartId]";
import RaceTestIndex from "./pages/raceTest";

const logger = new BasicLogger({ logLevel: import.meta.env.VITE_ENV_LOWER === 'prod' ? 0 : 2, logFormat: 'BROWSER' });

const router = createBrowserRouter([
  {
    path: "/",
    element: <Index />,
    errorElement: <ErrorPage />
  },
  {
    path: "/raceTest",
    element: <RaceTestIndex />,
    errorElement: <ErrorPage />
  },
  {
    path: "/job/:jobId",
    element: <JobDetail />,
    errorElement: <ErrorPage />
  },
  {
    path: "/job/:jobId/plan",
    element: <CreatePlanDetail />,
    errorElement: <ErrorPage />
  },
  {
    path: "/job/:jobId/edit-plan/:planId",
    element: <EditPlanDetail />,
    errorElement: <ErrorPage />
  },
  {
    path: "/job/:jobId/view-plan/:planId",
    element: <ViewPlanDetail />,
    errorElement: <ErrorPage />
  },
  {
    path: "/job/:jobId/plan/:planId/prestart",
    element: <CreatePrestartDetail />,
    errorElement: <ErrorPage />
  },
  {
    path: "/job/:jobId/plan/:planId/edit-prestart/:prestartId",
    element: <EditPrestartDetail />,
    errorElement: <ErrorPage />
  },
  {
    path: "/job/:jobId/plan/:planId/view-prestart/:prestartId",
    element: <ViewPrestartDetail />,
    errorElement: <ErrorPage />
  }
]);

const QueryClientProviderWrapper = ({ children }: PropsWithChildren) => {
  // --------------------------------------------
  const { queueSnackbar } = useSnackbar();
  const authDetail = useMSALPayload();

  // Add a ref to track if we're already handling a login redirect
  const loginRedirectInProgress = useRef(false);

  // Add a ref to store the current authDetail so it's always fresh in the error handler
  const authDetailRef = useRef(authDetail);
  authDetailRef.current = authDetail;

  const [queryClient] = useState(() => new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        // --------------------------------------------
        const { queryKey } = query;
        logger.error(`Error with query`, query);
        logger.error(`Error`, error);

        if (
          error instanceof AuthLibMSALTokenAcquisitionError
          || [
            'Unauthorized',
            'Could not get fresh ID token',
            'Token acquisition failed - interactive login required',
            'Failed to acquire token silently - interactive login required'
          ].includes(error.message)
        ) {
          // Use the ref to get the current authDetail instead of the stale closure value
          const { msal } = authDetailRef.current;

          logger.debug(msal.inProgress);
          logger.debug(loginRedirectInProgress.current);

          // Check both MSAL's inProgress state AND our local tracking
          if (msal.inProgress === "none" && !loginRedirectInProgress.current) {
            loginRedirectInProgress.current = true;
            queueSnackbar(`We need to be sure it's really you. Redirecting you to login`, 'warning', 'Unauthorised');

            logger.warn(`Redirecting to login`);
            logger.debug(`In progress status: ${msal.inProgress}`);

            // Use a try-catch to handle any potential errors from loginRedirect
            try {
              // --------------------------------------------
              logger.debug('Redirecting to login');
              msal.instance.loginRedirect({
                account: msal.accounts.length > 0 ? msal.accounts[0] : undefined,
                scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"]
              });
            } catch (redirectError) {
              // If loginRedirect fails, reset our tracking
              loginRedirectInProgress.current = false;
              logger.error('Login redirect failed:', redirectError);

              // If it's an interaction_in_progress error, that's expected
              if (redirectError instanceof Error &&
                redirectError.message.includes('interaction_in_progress')) {
                Sentry.captureMessage('App.tsx - Login redirect already in progress, ignoring duplicate call', 'info');
                logger.debug('Login redirect already in progress, ignoring duplicate call');
              } else {
                // Re-throw unexpected errors
                Sentry.addBreadcrumb({              // bread-crumb only, no issue
                  category: "App.tsx auth",
                  message: `Non-interaction error with loginRedirect in App.tsx`,
                  level:  "info",
                });
                Sentry.withScope((scope) => {
                  scope.setTag('queryKey', queryKey.join(':'));
                  scope.setContext('query', {
                    queryKey: query.queryKey,
                    queryHash: query.queryHash,
                    state: query.state
                  });
                  scope.setLevel('error');
                  Sentry.captureException(redirectError);
                });
                throw redirectError;
              }
            }
          } else {
            logger.info(`Login redirect already in progress (MSAL: ${msal.inProgress}, Local: ${loginRedirectInProgress.current})`);
          }
        }
        else if (queryKey.includes('identity')) {
          // --------------------------------------------
          // Capture in Sentry with context
          Sentry.withScope((scope) => {
            scope.setTag('queryKey', queryKey.join(':'));
            scope.setContext('query', {
              queryKey: query.queryKey,
              queryHash: query.queryHash,
              state: query.state
            });
            scope.setLevel(error.message.includes('Unauthorized') ? 'warning' : 'error');
            Sentry.captureException(error);
          });
          queueSnackbar(
            `There was an error getting your identity data. This is typically caused by a internet, cloud or software issue. Please contact support`,
            'error',
            'Error with identity',
            5000
          );
        }
        else if (queryKey.includes('job') && queryKey.length === 2) {
          // --------------------------------------------
          // Capture in Sentry with context
          Sentry.withScope((scope) => {
            scope.setTag('queryKey', queryKey.join(':'));
            scope.setContext('query', {
              queryKey: query.queryKey,
              queryHash: query.queryHash,
              state: query.state
            });
            scope.setLevel(error.message.includes('Unauthorized') ? 'warning' : 'error');
            Sentry.captureException(error);
          });
          queueSnackbar(`There was an error getting the detail for job ${queryKey[1]}. This is typically caused by a internet, cloud or software issue. Please contact support`, 'error', `Error with ${queryKey[1]}`, 5000);
        }
        else if (error.message.includes('404')) {
          // --------------------------------------------
          // Capture in Sentry with context
          Sentry.withScope((scope) => {
            scope.setTag('queryKey', queryKey.join(':'));
            scope.setContext('query', {
              queryKey: query.queryKey,
              queryHash: query.queryHash,
              state: query.state
            });
            scope.setLevel(error.message.includes('Unauthorized') ? 'warning' : 'error');
            Sentry.captureException(error);
          });
          queueSnackbar(
            `The data you have tried to access could not be found. It may have been deleted or a parent record related to it. If you need it, please contact support`,
            'error',
            `Record not found`,
            5000
          );
        }
        else {
          // -------------------------------------------- 
          // Capture in Sentry with context
          Sentry.withScope((scope) => {
            scope.setTag('queryKey', queryKey.join(':'));
            scope.setContext('query', {
              queryKey: query.queryKey,
              queryHash: query.queryHash,
              state: query.state
            });
            scope.setLevel(error.message.includes('Unauthorized') ? 'warning' : 'error');
            Sentry.captureException(error);
          });
          logger.error(error);
          queueSnackbar(
            `This is an unexpected error. Please contact support`,
            'error',
            'Unexpected error',
            5000
          );
        }
      }
    })
  }));

  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  )
}

export const ProtectedApp = () => {
  // --------------------------------------------

  const authDetail = useMSALPayload(logger);
  const authRequest = {
    account: authDetail.msal.accounts.length > 0 ? authDetail.msal.accounts[0] : undefined,
    scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"]
  };


  return (
    <>
      <MsalAuthenticationTemplate
        interactionType={MSALInteractionType.Silent}
        authenticationRequest={authRequest}
        errorComponent={(props) => (
          <MsalAuthErrorFallbackBoundary
            interactionType={MSALInteractionType.Redirect}
            request={authRequest}
            {...props}
          />
        )}
        loadingComponent={(msal) => <MsalLoader msal={msal} />}
      >
        <SnackbarProvider anchorOrigin={{ vertical: "top", horizontal: "center" }}>
          <QueryClientProviderWrapper>
            <ReactQueryDevtools initialIsOpen={false} />
            <RouterProvider router={router} />
          </QueryClientProviderWrapper>
        </SnackbarProvider>
      </MsalAuthenticationTemplate>
    </>
  )
};


function App() {
  // --------------------------------------------
  return (
    <UpdateServiceWorkerProvider>
      <ThemeProvider theme={lightProdTheme}>
        <GlobalStyles styles={{ body: { margin: '0px' } }} />
        <CssBaseline />
        <MSALTenantedApp
          logger={logger}
          logLevel={import.meta.env.VITE_ENV_LOWER === 'prod' ? 0 : 2}
          tenantId={import.meta.env.VITE_MS_TENANT_ID}
          authority={`https://login.microsoftonline.com/${import.meta.env.VITE_MS_TENANT_ID}`}
          clientId={import.meta.env.VITE_MS_CLIENT_ID}
          redirectUri={`${window.location.origin}`}
          cacheLocation="localStorage"
        >
          <ProtectedApp />
        </MSALTenantedApp>
      </ThemeProvider>
    </UpdateServiceWorkerProvider>
  );
}

const SentryErrorBoundary = Sentry.withErrorBoundary(App, {
  fallback: () => {
    // --------------------------------------------
    return (
      <Box p="20px">
        <Typography fontSize="1rem" color="black" fontWeight={800}>
          Something went wrong
        </Typography>
        <Typography fontSize="0.8rem" color="black" fontWeight={300}>
          This has been recorded and we will look into the issue
        </Typography>
        <FilledButton
          onClick={async () => {
            await handleRestartApp();
          }}>
          Restart app
        </FilledButton>
      </Box>
    );
  },
  showDialog: true
});

export default SentryErrorBoundary;

